import type { AccessScope, AuthenticatedSession } from './types';
import { businessRoleLabels, mapLegacyBusinessRole } from '@kop/contracts';
import { featureAccessProjectionStatus } from '../feature-access-projection';

export function isVisibleProductScope(scope: AccessScope) {
  if (scope.role === 'system' || scope.persona === 'system-admin')
    return scope.id === 'kop-system' && scope.role === 'system' &&
      scope.persona === 'system-admin' && scope.resource.kind === 'tenant';
  return true;
}

const productRoleLabels: Readonly<Record<string, string>> = {
  'site-manager': '사업장 관리자',
  'safety-manager': '안전보건 관리자',
  'site-staff': '공장 일반',
  'safety-staff': '안전보건 담당자',
  'partner-admin': '협력업체 관리자',
  'partner-worker': '협력업체 작업자',
  'hq-safety-admin': '본사 안전보건 관리자',
  'hq-auditor': '본사 일반',
  'hq-staff': '본사 일반',
  'system-admin': '전사 관리자',
};

export function productScopeRoleLabel(scope: AccessScope) {
  if (scope.developerReadOnly === true) return '개발자';
  if (featureAccessProjectionStatus(scope.featureAccess).kind === 'enforced' && scope.businessRole)
    return `${businessRoleLabels[scope.businessRole]}${scope.developer ? ' · 개발자' : ''}`;
  if (
    scope.role === 'partner' &&
    scope.persona === 'partner-admin' &&
    scope.resource.kind === 'contractor' &&
    scope.detail === 'system-partner-delegation'
  ) return '업체 대리업무';
  if (
    scope.resource.kind === 'site' &&
    (scope.role === 'hqSafety' || scope.role === 'hqAudit')
  ) return '본사 조회';
  return productPersonaLabel(scope.persona);
}

export function productPersonaLabel(persona: string) {
  return productRoleLabels[persona] ?? '업무 담당자';
}

export function projectVisibleProductSession(
  session: AuthenticatedSession,
): AuthenticatedSession | null {
  const visible = session.scopes.filter(isVisibleProductScope);
  const hasBusinessManagementScope = visible.some(scope => {
    const projection = featureAccessProjectionStatus(scope.featureAccess);
    return scope.businessRole && scope.developer && projection.kind === 'enforced' && projection.features.has('system-management');
  });
  const hasDeveloperHeadquarters = visible.some(scope => scope.developerReadOnly === true &&
    scope.resource.kind === 'tenant' && featureAccessProjectionStatus(scope.featureAccess).kind === 'enforced');
  const hasHeadquartersDuty = visible.some(scope => scope.resource.kind === 'tenant' &&
    Boolean(scope.businessRole) && featureAccessProjectionStatus(scope.featureAccess).kind === 'enforced');
  // Internal users work with company records from their own site workspace. An old
  // delegated company scope is not a separate contractor appointment.
  const siteRepresentative = (scope: AccessScope) => {
    const resource = scope.resource;
    if (resource.kind !== 'contractor' || !scope.businessRole || scope.businessRole === 'contractor-staff' ||
      featureAccessProjectionStatus(scope.featureAccess).kind !== 'enforced') return undefined;
    return visible.find(candidate => candidate.resource.kind === 'site' &&
      candidate.resource.tenantId === resource.tenantId && candidate.resource.siteId === resource.siteId &&
      candidate.businessRole === scope.businessRole &&
      featureAccessProjectionStatus(candidate.featureAccess).kind === 'enforced');
  };
  const scopes: AccessScope[] = [];
  const keys = new Map<string, number>();
  const keyFor = (scope: AccessScope) => JSON.stringify([scope.resource.tenantId, scope.resource.kind,
    scope.resource.kind === 'tenant' ? null : scope.resource.siteId,
    scope.resource.kind === 'contractor' ? scope.resource.contractorId : null, scope.businessRole]);
  for (const scope of visible) {
    const enforced = featureAccessProjectionStatus(scope.featureAccess).kind === 'enforced';
    if (siteRepresentative(scope)) continue;
    if (enforced && scope.developerReadOnly && scope.resource.kind === 'tenant' && hasHeadquartersDuty) continue;
    if (enforced && scope.businessRole === null && scope.developer && scope.resource.kind === 'tenant' &&
      (hasBusinessManagementScope || hasDeveloperHeadquarters) && scope.developerReadOnly !== true) continue;
    if (!enforced || !scope.businessRole) { scopes.push(scope); continue; }
    const key = keyFor(scope);
    const prior = keys.get(key);
    if (prior === undefined) { keys.set(key, scopes.length); scopes.push(scope); }
    // Prefer the actual business scope to an old duplicate HQ-view projection of it.
    else if (mapLegacyBusinessRole(scope.persona) === scope.businessRole &&
      mapLegacyBusinessRole(scopes[prior]!.persona) !== scope.businessRole) scopes[prior] = scope;
  }
  if (scopes.length === 0) return null;
  const activeScopeId = scopes.some((scope) => scope.id === session.activeScopeId)
    ? session.activeScopeId
    : (scopes.find(scope => {
        const original = visible.find(candidate => candidate.id === session.activeScopeId);
        if (!original) return false;
        const representative = siteRepresentative(original) ??
          (original.developerReadOnly && original.resource.kind === 'tenant'
            ? scopes.find(candidate => candidate.resource.kind === 'tenant' && candidate.businessRole)
            : undefined) ?? original;
        return representative.businessRole && keyFor(representative) === keyFor(scope);
      })?.id ?? scopes.find(
        (scope) =>
          scope.persona === 'safety-manager' &&
          scope.resource.kind === 'site',
      )?.id ?? scopes[0]!.id);
  return { ...session, scopes, activeScopeId };
}
